<template lang="pug">
el-drawer.approve-refund-drawer(
    :title="$t('Approve return')"
    :visible.sync="visibilityInternal"
    direction="rtl"
    size="40%"
    :wrapper-closable="false"
    @opened="onOpen"
    @closed="reset"
)
    section.body(v-if="cancellation")
        p.hint {{ $t('Confirm the return and the amount that will be refunded. Adjust if items arrived damaged or incomplete.') }}

        el-form(
            ref="approveForm"
            label-position="top"
            size="small"
            :model="form"
            :rules="rules"
        )
            .flex-row
                el-form-item.grow(:label="$t('Net amount to be credited')" prop="credited_amount_net")
                    el-input(
                        type="number"
                        v-model="creditedAmountNet"
                        step="0.01"
                        min="0"
                    )
                        template(slot="append")
                            i.el-icon-fa-euro-sign.fas(v-if="currencySign === '€'")
                            i.el-icon-fa-dollar-sign.fas(v-else-if="currencySign === '$'")
                            b.currency(v-else) {{ currencySign }}
                el-form-item.grow(:label="$t('Gross amount to be credited')" prop="credited_amount")
                    el-input(
                        type="number"
                        v-model="creditedAmount"
                        step="0.01"
                        min="0"
                    )
                        template(slot="append")
                            i.el-icon-fa-euro-sign.fas(v-if="currencySign === '€'")
                            i.el-icon-fa-dollar-sign.fas(v-else-if="currencySign === '$'")
                            b.currency(v-else) {{ currencySign }}

            el-form-item(:label="$t('Note to the customer')")
                el-input(
                    v-model="form.note"
                    type="textarea"
                    :rows="3"
                    :placeholder="$t('Two items arrived damaged — refund reduced by …')"
                    maxlength="1000"
                    show-word-limit
                )
                .help {{ $t('Appended to the status email so the customer sees why the amount changed.') }}

        //- Hint when the payment method requires a manual transfer.
        p.manual-hint(v-if="!autoRefundable")
            i.el-icon-fa-circle-info.fas
            | {{ $t('manual-payment-hint') }}

    .drawer-footer
        .left
            el-button(
                size="small"
                icon="el-icon-fa-times fas"
                @click="visibilityInternal = false"
            ) {{ $t('Cancel') }}
        .right.flex-row
            //- Bordered checkbox — same control type as the original
            //- partially-cancel drawer's "Automatically refund" toggle.
            //- Only visible when the gateway can settle automatically.
            el-checkbox(
                v-if="autoRefundable"
                border
                size="small"
                v-model="form.refund"
            ) {{ $t('Automatically refund') }}
            el-button(
                size="small"
                type="primary"
                icon="el-icon-fa-check fas"
                :loading="saving"
                @click="submit"
            ) {{ $t('Approve') }}
</template>

<i18n>
{
    "en": {
        "Approve return": "Approve return",
        "Approve": "Approve",
        "Automatically refund": "Automatically refund",
        "Confirm the return and the amount that will be refunded. Adjust if items arrived damaged or incomplete.": "Confirm the return and the amount that will be refunded. Adjust if items arrived damaged or incomplete.",
        "Appended to the status email so the customer sees why the amount changed.": "Appended to the status email so the customer sees why the amount changed.",
        "manual-payment-hint": "Refund is recorded on approval; the actual bank transfer / cash payout must be issued manually via the original payment channel.",
        "Gross amount to be credited": "Gross amount to be credited",
        "Net amount to be credited": "Net amount to be credited",
        "Note to the customer": "Note to the customer",
        "Two items arrived damaged — refund reduced by …": "Two items arrived damaged — refund reduced by …",
        "Cancel": "Cancel",
        "Return approved": "Return approved",
        "Approval failed": "Approval failed"
    },
    "de": {
        "Approve return": "Widerruf genehmigen",
        "Approve": "Genehmigen",
        "Automatically refund": "Automatisch erstatten",
        "Confirm the return and the amount that will be refunded. Adjust if items arrived damaged or incomplete.": "Bestätigen Sie den Widerruf und den Erstattungsbetrag. Passen Sie den Betrag an, wenn Ware beschädigt oder unvollständig eingegangen ist.",
        "Appended to the status email so the customer sees why the amount changed.": "Wird an die Statusbenachrichtigung angehängt, damit der Kunde sieht, warum der Betrag abweicht.",
        "manual-payment-hint": "Die Erstattung wird mit der Genehmigung verbucht; der tatsächliche Geldfluss (Überweisung, Bareinzahlung etc.) muss manuell über den ursprünglichen Zahlungsweg ausgelöst werden.",
        "Gross amount to be credited": "Brutto Gutschrift-Betrag",
        "Net amount to be credited": "Netto Gutschrift-Betrag",
        "Note to the customer": "Hinweis an den Kunden",
        "Two items arrived damaged — refund reduced by …": "Zwei Artikel beschädigt eingetroffen — Erstattung reduziert um …",
        "Cancel": "Abbrechen",
        "Return approved": "Widerruf genehmigt",
        "Approval failed": "Genehmigung fehlgeschlagen"
    }
}
</i18n>

<script>
import _ from 'lodash';
import { currencySymbol } from '@/filters';
import { adminClient as apolloClient } from '@/store/apollo';
import TransitionCancellationGql from '@/extensions/shop/store/graphql/mutations/TransitionCancellation.gql';

export default {
    name: 'ApproveRefundDrawer',
    props: {
        visibility: { type: Boolean, default: false },
        cancellation: { type: Object, default: null },
        autoRefundable: { type: Boolean, default: false },
        currency: { type: String, default: '€' },
    },
    data: () => ({
        form: {
            credited_amount: 0,
            credited_amount_net: 0,
            note: '',
            // Default ON when the gateway can auto-settle; the checkbox is
            // hidden otherwise so the value doesn't matter on the manual
            // path (backend records 'succeeded-manual' anyway).
            refund: true,
        },
        // Tax rate derived from the cancellation's original gross/net pair —
        // single number, used to keep the two inputs in sync as the admin
        // types in either field. Same pattern as PartiallyCancelForm.
        taxRate: 0,
        saving: false,
    }),
    computed: {
        visibilityInternal: {
            get() { return this.visibility; },
            set(value) { this.$emit('update:visibility', value); },
        },
        // vee-validate v2 pipe-string rules. Max bound = the cancellation's
        // ORIGINAL credited_amount (gross / net respectively). The admin
        // can shrink the refund (goods arrived damaged → reduce) but never
        // grow it past the customer's original claim, even when the purchase
        // still has more headroom available. This matches the preset values
        // shown in the form so "preset == max" matches the user's mental
        // model. The wider purchase-level remaining_refundable cap still
        // applies upstream via the create-cancellation form.
        rules() {
            const originalGross = parseFloat(_.get(this.cancellation, 'credited_amount')) || 0;
            const originalNet = parseFloat(_.get(this.cancellation, 'credited_amount_net')) || 0;
            return {
                credited_amount: `required|min_value:0|max_value:${originalGross}`,
                credited_amount_net: `required|min_value:0|max_value:${originalNet}`,
            };
        },
        // Parent (Documents.vue → Returns.vue) hands us an ISO code
        // ("EUR" / "USD" / …). Convert via the same filter
        // PartiallyCancelForm uses so the el-input append shows the symbol
        // (€ / $) or falls back to the bare ISO for currencies without a
        // common glyph.
        currencySign() {
            return currencySymbol(this.currency) || this.currency || '';
        },
        creditedAmountNet: {
            get() {
                return this.form.credited_amount_net || undefined;
            },
            set(value) {
                const net = parseFloat(value) || 0;
                this.$set(this.form, 'credited_amount_net', net);
                this.$set(this.form, 'credited_amount', _.round(net * (1 + this.taxRate / 100), 2));
            },
        },
        creditedAmount: {
            get() {
                return this.form.credited_amount || undefined;
            },
            set(value) {
                const gross = parseFloat(value) || 0;
                this.$set(this.form, 'credited_amount', gross);
                const denom = 1 + this.taxRate / 100;
                this.$set(this.form, 'credited_amount_net', denom > 0 ? _.round(gross / denom, 2) : gross);
            },
        },
    },
    methods: {
        onOpen() {
            const gross = parseFloat(_.get(this.cancellation, 'credited_amount')) || 0;
            const net = parseFloat(_.get(this.cancellation, 'credited_amount_net')) || 0;
            // Derive the effective tax rate so the gross/net pair stays
            // consistent as the admin edits either side.
            this.taxRate = net > 0 ? _.round((gross - net) / net * 100, 6) : 0;
            this.form = {
                credited_amount: gross,
                credited_amount_net: net,
                note: '',
                refund: true,
            };
        },
        reset() {
            this.form = { credited_amount: 0, credited_amount_net: 0, note: '', refund: true };
            this.taxRate = 0;
            this.saving = false;
        },
        async submit() {
            if (!this.cancellation) return;
            // Block the gateway call when the typed amount exceeds
            // remaining_refundable (or any other rule fails).
            const form = this.$refs.approveForm;
            if (form && typeof form.validate === 'function') {
                const valid = await new Promise(resolve => form.validate(ok => resolve(!!ok)));
                if (!valid) return;
            }
            this.saving = true;
            const variables = {
                id: parseInt(this.cancellation.id),
                status: 'approved',
                // Auto-refundable: honour checkbox. Manual: always true so the
                // backend records 'succeeded-manual' on the approval row.
                refund: this.autoRefundable ? !!this.form.refund : true,
                credited_amount: parseFloat(this.form.credited_amount) || 0,
                credited_amount_net: parseFloat(this.form.credited_amount_net) || 0,
            };
            if (this.form.note && this.form.note.trim()) {
                variables.note = this.form.note.trim();
            }
            try {
                await apolloClient.mutate({
                    mutation: TransitionCancellationGql,
                    variables,
                });
                this.$notify.success({
                    title: this.$t('Return approved'),
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 4000,
                });
                this.$emit('success');
                this.visibilityInternal = false;
            } catch (err) {
                console.error(err);
                this.$notify.error({
                    title: this.$t('Approval failed'),
                    message: err.message || '',
                    position: 'bottom-left',
                    customClass: 'dark',
                    duration: 6000,
                });
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style lang="sass" scoped>
.approve-refund-drawer
    ::v-deep .el-drawer__body
        display: flex
        flex-direction: column

.body
    flex: 1
    padding: 16px 20px
    overflow-y: auto

.hint
    color: var(--color-text-secondary, #606266)
    font-size: 13px
    line-height: 1.4
    margin-bottom: 16px

// Inline help under an input — small, muted gray. Explicit line-height
// because el-form-item__content inherits 36px otherwise.
.help
    display: block
    margin-top: 4px
    font-size: 12px
    line-height: 1.4
    color: var(--color-text-muted, #909399)

.flex-row
    display: flex
    gap: 16px
    .grow
        flex: 1

.manual-hint
    margin-top: 16px
    padding: 8px 12px
    background: rgba(160, 174, 192, 0.12)
    border-left: 3px solid #a0aec0
    color: var(--color-text-secondary, #606266)
    font-size: 12px
    line-height: 1.4
    i
        margin-right: 6px
        color: #a0aec0

.drawer-footer
    padding: 12px 20px
    border-top: 1px solid var(--border-color, #ebeef5)
    display: flex
    align-items: center
    justify-content: space-between
    .right
        gap: 8px
        align-items: center
</style>
